import { ChakraV3 } from '@piccolohealth/ui';
import {
  constructZodLiteralUnionType,
  type DateTime,
  dateTimeSchema,
  z,
} from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  type RecurrenceOption,
  recurrenceOptions,
} from '../../components/forms/HookedTaskRecurrenceSelect';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useCreateParticipantTaskMutation } from '../../graphql/hooks/useCreateParticipantTaskMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantTaskForm } from './ParticipantTaskForm';

type FormValues = {
  name: string;
  dueDate?: DateTime | null;
  requiresDueDate: boolean;
  recurrence?: RecurrenceOption | null;
  labelIds: string[];
  assigneeIds: string[];
};

const schema: z.ZodType<FormValues> = z.object({
  name: z.string().min(1, 'Task name is required'),
  requiresDueDate: z.boolean(),
  dueDate: dateTimeSchema.optional(),
  labelIds: z.array(z.string()),
  assigneeIds: z.array(z.string()),
  recurrence: constructZodLiteralUnionType(recurrenceOptions.map((option) => option.raw)),
});

interface ParticipantNoteCreateModalProps {
  participantId: string;
}

export const ParticipantTaskCreateModal = createModal<ParticipantNoteCreateModalProps>((props) => {
  const { participantId, modal } = props;
  const { errorToast, successToast, organization } = useAppContext();

  const { hide, remove, visible } = modal;

  const mutation = useCreateParticipantTaskMutation();

  const methods = useForm<FormValues>({
    defaultValues: {
      name: '',
      requiresDueDate: false,
      dueDate: undefined,
      labelIds: [],
      assigneeIds: [],
      recurrence: 'none',
    },
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = methods;

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      try {
        const recurrence =
          values.recurrence === 'none'
            ? undefined
            : recurrenceOptions.find((option) => option.raw === values.recurrence);

        await mutation.mutateAsync({
          organizationId: organization.id,
          participantId,
          request: {
            ...values,
            recurrence: recurrence ? { intervalDays: recurrence.intervalDays } : undefined,
          },
        });
        successToast('Task created successfully');
        hide();
      } catch (error) {
        errorToast(`Error creating task: ${error.message}`);
      }
    },
    [errorToast, hide, mutation, organization.id, participantId, successToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Create a new task'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantTaskForm participantId={participantId} />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button size='sm' onClick={hide} variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton size='sm'>Create</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
